/**
 * Pinned from `/Users/saher/code/cafe/Tarabeeza-Backend/src/modules/scheduling/`
 * (read-only source — never edit that checkout from here):
 *   - scheduling.controller.ts + scheduling.service.ts + dto/scheduling.dto.ts
 *   - ../../shared/domain/scheduling.ts (the pure conflict detector)
 *   - prisma/schema.prisma (Schedule, ScheduledShift, ShiftTemplate,
 *     EmployeeAvailability, ShiftSwapRequest, and their four enums)
 * One file, so a later drift on that side is a one-file fix here.
 *
 * ADR-0008 in the sentences that shape this module: a shift is authored as a
 * LOCAL date and wall clock and stored as an absolute instant, resolved
 * against the branch's IANA timezone at the moment of writing — which is why
 * every screen built on these types shows a branch's roster in the branch's
 * own clock, not the browser's. Conflicts are decided by
 * `detectScheduleConflicts`, which reads no database, so this screen shows
 * the caller the same rules `assertAcceptable` enforces server-side rather
 * than a second implementation of them that drifts — this file exists so
 * that promise stays true: BLOCKING conflicts refuse the write outright,
 * ADVISORY ones come back once as a 409 and again, accepted, on retry
 * (`acceptWarnings: true`).
 *
 * `scheduling.publish` notifies: publishing a period emits `SCHEDULE_PUBLISHED`
 * with every affected `employeeId`, once for the whole period, not once per
 * shift — see the confirm copy in `RosterTab.tsx` before the click, not a
 * toast after it.
 *
 * Two contract gaps worth knowing before you extend this file:
 *   - There is no dry-run "check conflicts" endpoint. The only way to see them
 *     is to attempt the write; a BLOCKING or un-accepted ADVISORY conflict
 *     answers 409 with the same `ScheduleConflictReport[]` shape the pure
 *     detector produces, joined into one message by `assertAcceptable`. This
 *     file's `parseConflictMessage` undoes that join well enough to render a
 *     list, but it is guessing at a boundary the backend does not expose as
 *     structured data over HTTP.
 *   - A manager can ADD another employee's availability
 *     (`POST /scheduling/employees/:employeeId/availability`) but the
 *     controller exposes no matching DELETE for anyone but the employee
 *     themselves (`DELETE /scheduling/me/availability/:id`, `view_own`).
 *     `AvailabilityTab` is add-only for exactly this reason — a delete button
 *     here would call an endpoint that does not exist.
 */
import { apiError, apiStatus } from './lib';

export { apiError, apiStatus };

// ---------------------------------------------------------------- enums

export const ScheduleStatus = { DRAFT: 'DRAFT', PUBLISHED: 'PUBLISHED' } as const;
export type ScheduleStatus = (typeof ScheduleStatus)[keyof typeof ScheduleStatus];

export const ScheduledShiftStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  /** Nobody assigned; a member of staff holding `scheduling.view_own` may claim it. */
  OPEN: 'OPEN',
  CANCELLED: 'CANCELLED',
} as const;
export type ScheduledShiftStatus = (typeof ScheduledShiftStatus)[keyof typeof ScheduledShiftStatus];

export const AvailabilityKind = { AVAILABLE: 'AVAILABLE', UNAVAILABLE: 'UNAVAILABLE' } as const;
export type AvailabilityKind = (typeof AvailabilityKind)[keyof typeof AvailabilityKind];

export const ShiftSwapStatus = {
  PENDING: 'PENDING',
  /** The named colleague agreed; still needs a manager. */
  ACCEPTED: 'ACCEPTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const;
export type ShiftSwapStatus = (typeof ShiftSwapStatus)[keyof typeof ShiftSwapStatus];

// ------------------------------------------------------------- conflicts

/** `ScheduleConflict` in `shared/domain/scheduling.ts`. */
export const ScheduleConflictType = {
  OVERLAPPING_SHIFT: 'OVERLAPPING_SHIFT',
  EMPLOYEE_UNAVAILABLE: 'EMPLOYEE_UNAVAILABLE',
  EXCEEDS_DAILY_HOURS: 'EXCEEDS_DAILY_HOURS',
  EXCEEDS_WEEKLY_HOURS: 'EXCEEDS_WEEKLY_HOURS',
  INSUFFICIENT_REST: 'INSUFFICIENT_REST',
  OUTSIDE_EMPLOYMENT: 'OUTSIDE_EMPLOYMENT',
  NOT_ASSIGNED_TO_BRANCH: 'NOT_ASSIGNED_TO_BRANCH',
  NOT_EMPLOYABLE: 'NOT_EMPLOYABLE',
} as const;
export type ScheduleConflictType = (typeof ScheduleConflictType)[keyof typeof ScheduleConflictType];

export type ConflictSeverity = 'BLOCKING' | 'ADVISORY';

export interface ScheduleConflictReport {
  type?: ScheduleConflictType;
  severity: ConflictSeverity;
  message: string;
  conflictsWithId?: string;
}

/**
 * `assertAcceptable` joins every conflict's `message` with `; ` into one
 * `ConflictException` string, and appends
 * `" — send acceptWarnings to save anyway"` only when every conflict in the
 * batch is advisory (a single BLOCKING one refuses outright, unconditionally,
 * with no such suffix). That suffix is the only signal this screen gets for
 * "retryable" versus "refused" — there is no structured field for it. Good
 * enough to drive the UI; not a substitute for the typed report the pure
 * detector actually returns.
 */
const ACCEPT_SUFFIX = ' — send acceptWarnings to save anyway';

export function parseConflictMessage(message: string): {
  items: string[];
  retryable: boolean;
} {
  const retryable = message.endsWith(ACCEPT_SUFFIX);
  const body = retryable ? message.slice(0, -ACCEPT_SUFFIX.length) : message;
  const items = body
    .split('; ')
    .map((s) => s.trim())
    .filter(Boolean);
  return { items, retryable };
}

// ------------------------------------------------------------------ refs

export interface BranchRef {
  id: string;
  name: string;
  code: string;
  timezone: string;
}

export interface RoleRef {
  id: string;
  key: string;
  name: string;
}

export interface EmployeeRef {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string | null;
}

// -------------------------------------------------------------- shifts

/** `SHIFT_INCLUDE` in `scheduling.service.ts`. */
export interface ScheduledShift {
  id: string;
  scheduleId: string;
  branchId: string;
  employeeId: string | null;
  roleId: string | null;
  templateId: string | null;
  status: ScheduledShiftStatus;
  /** Absolute instant. Duration is always `endsAt - startsAt`. */
  startsAt: string;
  endsAt: string;
  businessDate: string;
  timezone: string;
  breakMinutes: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  employee: EmployeeRef | null;
  role: RoleRef | null;
  branch: BranchRef;
}

export interface CreateShiftInput {
  employeeId?: string;
  roleId?: string;
  templateId?: string;
  date: string;
  startTime: string;
  endTime: string;
  breakMinutes?: number;
  notes?: string;
  acceptWarnings?: boolean;
}

export type UpdateShiftInput = Partial<Omit<CreateShiftInput, 'date' | 'startTime' | 'endTime'>> & {
  employeeId?: string | null;
  date?: string;
  startTime?: string;
  endTime?: string;
  status?: typeof ScheduledShiftStatus.CANCELLED | typeof ScheduledShiftStatus.OPEN;
};

/** `POST /scheduling/schedules/:id/shifts` and `PATCH /scheduling/shifts/:id` both answer this. */
export interface ShiftWriteResult {
  shift: ScheduledShift;
  warnings: ScheduleConflictReport[];
}

// ----------------------------------------------------------------- schedules

export interface Schedule {
  id: string;
  branchId: string;
  name: string | null;
  /** Inclusive local date. */
  startDate: string;
  /** Inclusive local date. */
  endDate: string;
  status: ScheduleStatus;
  publishedAt: string | null;
  publishedById: string | null;
  notes: string | null;
  branch: BranchRef;
  _count?: { shifts: number };
}

export interface ScheduleDetail extends Omit<Schedule, '_count'> {
  shifts: ScheduledShift[];
}

export interface CreateScheduleInput {
  branchId: string;
  name?: string;
  startDate: string;
  endDate: string;
  notes?: string;
}

// -------------------------------------------------------------------- roster

/** `GET /scheduling/roster` — read-only, and independent of any one Schedule id. */
export interface RosterDay {
  date: string;
  shifts: ScheduledShift[];
  scheduledMinutes: number;
}

export interface Roster {
  branchId: string;
  from: string;
  to: string;
  days: RosterDay[];
}

/**
 * `GET /scheduling/me` — a member of staff's own published shifts, and the
 * open ones they could claim. DRAFT shifts are absent by design: a
 * half-built rota is not something to plan a week around, which is why
 * publish exists at all (`SchedulingService.myRoster`'s own comment).
 */
export interface MyRoster {
  employeeId: string;
  from: string;
  to: string;
  shifts: ScheduledShift[];
  openShifts: ScheduledShift[];
}

// ----------------------------------------------------------------- templates

export interface ShiftTemplate {
  id: string;
  /** Null = every branch in the tenant. */
  branchId: string | null;
  name: string;
  roleId: string | null;
  role: RoleRef | null;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  isActive: boolean;
}

export interface CreateTemplateInput {
  name: string;
  branchId?: string;
  roleId?: string;
  startTime: string;
  endTime: string;
  breakMinutes?: number;
}

export type UpdateTemplateInput = Partial<Omit<CreateTemplateInput, 'branchId'>> & {
  isActive?: boolean;
};

export interface ApplyTemplateInput {
  templateId: string;
  dates: string[];
  employeeId?: string;
  acceptWarnings?: boolean;
}

export interface ApplyTemplateResult {
  created: number;
  shifts: ScheduledShift[];
  warnings: { date: string; conflicts: ScheduleConflictReport[] }[];
}

// --------------------------------------------------------------- availability

/** Weekly XOR dated — the backend's own CHECK constraint says so too. */
export interface AvailabilityWindow {
  id: string;
  employeeId: string;
  kind: AvailabilityKind;
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
  startsAt: string | null;
  endsAt: string | null;
  reason: string | null;
}

export interface CreateAvailabilityInput {
  kind: AvailabilityKind;
  dayOfWeek?: number;
  startTime?: string;
  endTime?: string;
  startsAt?: string;
  endsAt?: string;
  reason?: string;
}

// --------------------------------------------------------------------- swaps

/**
 * `POST /scheduling/me/swaps` — asking, not deciding. `scheduling.swap_request`
 * is deliberately a narrower key than `scheduling.swap_approve` (`SwapsTab`'s
 * own comment): someone who could both ask and sign for their own shift would
 * make the manager's approval meaningless.
 *
 * There is no `GET /scheduling/me/swaps` in this API — the only read surface
 * for a swap's outcome is the manager's queue (`SwapsTab`, `scheduling.view`).
 * The dialog that posts this does not pretend to track the request
 * afterwards for that reason: inventing a local "pending" status a page
 * reload cannot refresh from the server is the "shows done to one person,
 * pending to the other" failure this feature has to avoid, not a shortcut
 * past it.
 */
export interface RequestSwapInput {
  shiftId: string;
  targetEmployeeId?: string;
  reason?: string;
}

export interface ShiftSwapRequest {
  id: string;
  shiftId: string;
  requestedById: string;
  targetEmployeeId: string | null;
  status: ShiftSwapStatus;
  reason: string | null;
  decidedById: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  shift: ScheduledShift;
  requestedBy: EmployeeRef;
  target: EmployeeRef | null;
}

export interface DecideSwapInput {
  targetEmployeeId?: string;
  note?: string;
}

// -------------------------------------------------------------------- labour

/** Hours, deliberately never cost — `scheduling.view` does not imply `compensation.view`. */
export interface LabourReportDay {
  date: string;
  scheduledMinutes: number;
  actualMinutes: number;
  overtimeMinutes: number;
  /** Positive = worked more than rostered. */
  varianceMinutes: number;
  scheduledHeadcount: number;
  actualHeadcount: number;
}

export interface LabourReport {
  branchId: string;
  branchName: string;
  from: string;
  to: string;
  days: LabourReportDay[];
  totals: {
    scheduledMinutes: number;
    actualMinutes: number;
    overtimeMinutes: number;
    varianceMinutes: number;
  };
}

// ----------------------------------------------------------------- helpers

/** `120m` → `2.0h`, matching the `hours()` helper the backend's own messages use. */
export function fmtHours(minutes: number): string {
  return `${(minutes / 60).toFixed(1)}h`;
}

export function employeeName(e: EmployeeRef | null | undefined): string | null {
  if (!e) return null;
  return `${e.firstName} ${e.lastName ?? ''}`.trim();
}

// ------------------------------------------------------------- date math
//
// Plain calendar-date arithmetic on `YYYY-MM-DD` strings, done through
// `Date.UTC` so the browser's own local timezone never enters it — these are
// the local dates a schedule is authored in (ADR-0008 §3), not instants, and
// the backend is what resolves them against the BRANCH's zone. `dayOfWeek`
// matches `CreateAvailabilityDto`: 0 = Sunday … 6 = Saturday.

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function toUtcDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDaysIso(iso: string, days: number): string {
  const d = toUtcDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

export function dayOfWeekIso(iso: string): number {
  return toUtcDate(iso).getUTCDay();
}

export function startOfWeekIso(iso: string): string {
  return addDaysIso(iso, -dayOfWeekIso(iso));
}

export function startOfMonthIso(iso: string): string {
  const d = toUtcDate(iso);
  return toIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
}

export function endOfMonthIso(iso: string): string {
  const d = toUtcDate(iso);
  return toIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
}

export function datesBetweenIso(from: string, to: string): string[] {
  const out: string[] = [];
  let cur = from;
  let guard = 0;
  while (cur <= to && guard < 400) {
    out.push(cur);
    cur = addDaysIso(cur, 1);
    guard += 1;
  }
  return out;
}

const WEEKDAY_SHORT = [
  'common.weekday.sun',
  'common.weekday.mon',
  'common.weekday.tue',
  'common.weekday.wed',
  'common.weekday.thu',
  'common.weekday.fri',
  'common.weekday.sat',
] as const;

export function weekdayKey(iso: string): string {
  return WEEKDAY_SHORT[dayOfWeekIso(iso)];
}

/** Same table, addressed directly by `dayOfWeek` (0 = Sunday … 6 = Saturday) —
 *  for `CreateAvailabilityDto`'s weekly form, which has no date to derive it from. */
export function weekdayKeyForIndex(dayOfWeek: number): string {
  return WEEKDAY_SHORT[dayOfWeek] ?? WEEKDAY_SHORT[0];
}

/**
 * A shift's wall clock, in the ZONE IT WAS AUTHORED IN — never the browser's.
 * `startsAt`/`endsAt` are instants; rendering them with the viewer's local
 * timezone would show a Cairo shift's 18:00 as 16:00 to a manager covering
 * from Riyadh, on the same data the branch's own staff sees correctly.
 */
export function formatWallTime(iso: string, timeZone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso));
}

/**
 * A client-side, advisory-only echo of `overlapConflicts` in
 * `shared/domain/scheduling.ts` — the same "each starts before the other
 * ends" rule, run over whatever the visible roster already loaded, so an
 * overlap is a red chip on the grid the moment both shifts are on screen
 * rather than something a manager discovers on the 409 from the dialog. It is
 * a hint, not the source of truth: the server re-runs the real detector
 * (with availability, rest and hour limits it has no reason to duplicate
 * here) on every write, and always wins.
 */
export function clientOverlapWarnings(shifts: ScheduledShift[]): Set<string> {
  const warned = new Set<string>();
  const byEmployee = new Map<string, ScheduledShift[]>();
  for (const s of shifts) {
    if (!s.employeeId || s.status === ScheduledShiftStatus.CANCELLED) continue;
    const list = byEmployee.get(s.employeeId) ?? [];
    list.push(s);
    byEmployee.set(s.employeeId, list);
  }
  for (const list of byEmployee.values()) {
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const a = list[i];
        const b = list[j];
        if (a.startsAt < b.endsAt && b.startsAt < a.endsAt) {
          warned.add(a.id);
          warned.add(b.id);
        }
      }
    }
  }
  return warned;
}
